/* =====================================================================
 * WorkspaceModeChrome — route-scoped workspace chrome.
 *
 * Owns the shared visual/runtime chrome used by workspace-mode pages:
 * wallpaper, top bar, composer placement, drag/drop, and page boundary.
 * Page selection stays outside this component.
 * ===================================================================== */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Menu, PanelLeft } from 'lucide-react';
import {
  emitAppEvent,
  onAppEvent,
  resolveEffectiveWallpaperConfig,
  useChatStore,
  useUIStore,
  useUserStore,
  useWorkspaceNavigationStore,
} from '@auw/stores';
import { WORKSPACE_MODES, type WorkspaceMode } from '@auw/workspace-utils';
import {
  AppErrorBoundary,
  Button,
  DropOverlay,
  Tooltip,
} from '@auw/workspace-ui';
import { useFeature } from '@auw/feature-flags';
import { WorkspaceComposer } from '../workspace/composer/WorkspaceComposer';
import { useRouteSyncToStore, useStoreSyncToRoute } from '../routing/useRouteSync';
import { useWorkspaceRouteMode } from '../workspace/navigation/useWorkspaceRoute';

const Container = styled.div<{ $dragging: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
  isolation: isolate;
  transition: background ${({ theme }) => theme.transitions.normal};

  ${({ $dragging, theme }) =>
    $dragging &&
    css`
      background: ${theme.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.03)'
        : 'rgba(0, 0, 0, 0.02)'};
    `}
`;

const WallpaperBg = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const WallpaperImg = styled.div`
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const TopBar = styled.div<{ $desktopHidden?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  min-height: 48px;
  flex-shrink: 0;

  ${({ $desktopHidden, theme }) =>
    $desktopHidden &&
    css`
      display: none;
      @media (max-width: ${theme.breakpoints.mobile}) {
        display: flex;
      }
    `}
`;

const TopBarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InputAreaWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
  padding-top: 24px;
`;

const ScrollToBottomBtn = styled.button<{ $visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: all ${({ theme }) => theme.transitions.fast};
  z-index: 5;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const DisclaimerFooter = styled.div`
  text-align: center;
  padding: 4px 16px 8px;
  font-size: 0.75rem;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.94)' : 'rgba(0,0,0,0.88)'};
  flex-shrink: 0;
  letter-spacing: -0.01em;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 0.6875rem;
    padding: 3px 12px 6px;
  }
`;

const MobilePageSidebarBtn = styled.button`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: inline-flex;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    border: 1px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.56)'};
    border-radius: 999px;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(18,20,24,0.42)' : 'rgba(255,255,255,0.38)'};
    color: ${({ theme }) => theme.colors.textSecondary};
    cursor: pointer;
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 10px 24px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.05)'
        : '0 10px 24px rgba(15,23,42,0.10), inset 0 1px 0 rgba(255,255,255,0.48)'};
    backdrop-filter: blur(18px) saturate(170%);
    -webkit-backdrop-filter: blur(18px) saturate(170%);
    transition:
      background ${({ theme }) => theme.transitions.fast},
      color ${({ theme }) => theme.transitions.fast},
      border-color ${({ theme }) => theme.transitions.fast},
      box-shadow ${({ theme }) => theme.transitions.fast};

    &:hover {
      background: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(48,51,58,0.68)' : 'rgba(255,255,255,0.52)'};
      border-color: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.74)'};
      color: ${({ theme }) => theme.colors.textPrimary};
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.accent};
      outline-offset: 2px;
    }
  }
`;

export interface WorkspaceModeChromeSlots {
  readonly inputSlot?: React.ReactNode;
}

export interface WorkspaceModeChromeProps {
  readonly mode?: WorkspaceMode;
  readonly children: (slots: WorkspaceModeChromeSlots) => React.ReactNode;
}

export const WorkspaceModeChrome: React.FC<WorkspaceModeChromeProps> = ({
  mode,
  children,
}) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const sidebarOpen = useChatStore((s) => s.sidebarOpen);
  const setSidebarOpen = useChatStore((s) => s.setSidebarOpen);
  const activeMode = useChatStore((s) => s.activeMode);
  const routeMode = useWorkspaceRouteMode();
  const currentWorkspaceMode = mode ?? routeMode ?? activeMode;
  const user = useUserStore((s) => s.user);
  const onboardingPending = user?.onboardingCompleted === false;
  const darkMode = useUIStore((s) => s.darkMode);
  const wallpaper = useUIStore((s) => s.wallpaper);
  const wallpaperExplicit = useUIStore((s) => s.wallpaperExplicit);
  const wallpaperFeatureEnabled = useFeature('workspace.wallpaper');
  const fileDropEnabled = useFeature('workspace.fileDrop');
  const aiDisclaimerEnabled = useFeature('workspace.aiDisclaimer');
  const [scrollBtnVisible, setScrollBtnVisible] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);

  const effectiveWallpaper = resolveEffectiveWallpaperConfig({
    wallpaper,
    wallpaperExplicit,
    darkMode,
  });

  const showWallpaper = !!(
    wallpaperFeatureEnabled &&
    effectiveWallpaper?.imageData &&
    (effectiveWallpaper.applyToAll || effectiveWallpaper.pages.includes(currentWorkspaceMode))
  );

  const modeInfo = WORKSPACE_MODES[currentWorkspaceMode];
  // 페이지가 화면 상태에 따라 composer 를 일시 억제할 수 있다 (기본 false).
  // GTC 에이전트는 분석 전 시작 페이지에서 입력창을 숨기고, 분석 후 후속
  // 채팅에서만 노출한다. showInput 은 모드 고정값이라 이 플래그가 필요하다.
  const composerSuppressed = useWorkspaceNavigationStore(
    (s) => s.composerSuppressedByMode[currentWorkspaceMode] ?? false,
  );
  const showComposer = modeInfo.showInput && !composerSuppressed;
  // 페이지 내부 목록 패널을 갖는 모드. false 면 composer 가
  // WorkspaceModeLayout **바깥**에 형제로 렌더되어(아래 `!hasPageListSidebar`
  // 분기) 목록 패널의 세로 공간을 빼앗아 하단이 잘린다.
  // 모드를 새로 추가할 때 여기 누락하면 그 증상이 나온다.
  const hasPageListSidebar =
    currentWorkspaceMode === 'agent'
    || currentWorkspaceMode === 'live-agent'
    || currentWorkspaceMode === 'gtc-agent'
    || currentWorkspaceMode === 'document'
    || currentWorkspaceMode === 'message'
    || currentWorkspaceMode === 'storage'
    || currentWorkspaceMode === 'tools';

  useRouteSyncToStore();
  useStoreSyncToRoute();

  useEffect(() => {
    return onAppEvent('agent:scrollState', (detail) => {
      const { isEmpty, isNearBottom, streaming } = detail;
      setScrollBtnVisible(currentWorkspaceMode === 'agent' && !isEmpty && !isNearBottom && !streaming);
    });
  }, [currentWorkspaceMode]);

  const hasFiles = (e: React.DragEvent) =>
    e.dataTransfer.types.includes('Files');

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!hasFiles(e)) return;
    dragCounter.current += 1;
    if (dragCounter.current === 1) setDragging(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!hasFiles(e)) return;
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length === 0) return;
    emitAppEvent('chat:addFiles', { files });
  }, []);

  const dropZoneActive = modeInfo.showDragDrop && fileDropEnabled;
  const pageInputSlot =
    hasPageListSidebar && showComposer ? (
      <>
        <InputAreaWrapper>
          <ScrollToBottomBtn
            $visible={scrollBtnVisible}
            onClick={() => emitAppEvent('agent:scrollToBottom')}
          >
            ↓
          </ScrollToBottomBtn>
          <WorkspaceComposer />
        </InputAreaWrapper>
        {aiDisclaimerEnabled && (
          <DisclaimerFooter>
            {t('chat.aiDisclaimer')}
          </DisclaimerFooter>
        )}
      </>
    ) : undefined;

  return (
    <Container
      $dragging={dropZoneActive && dragging}
      onDragEnter={dropZoneActive ? handleDragEnter : undefined}
      onDragOver={dropZoneActive ? handleDragOver : undefined}
      onDragLeave={dropZoneActive ? handleDragLeave : undefined}
      onDrop={dropZoneActive ? handleDrop : undefined}
    >
      {showWallpaper && (
        <WallpaperBg>
          <WallpaperImg
            style={{
              backgroundImage: `url(${effectiveWallpaper!.imageData})`,
              opacity: effectiveWallpaper!.opacity / 100,
            }}
          />
        </WallpaperBg>
      )}

      {modeInfo.showTopBar && (
        <TopBar $desktopHidden={hasPageListSidebar}>
          <TopBarLeft>
            {!onboardingPending && !sidebarOpen && (
              <Tooltip label={t('tooltip.sidebar')} position="bottom" align="start">
                <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)}>
                  <PanelLeft size={18} />
                </Button>
              </Tooltip>
            )}
            {!onboardingPending && hasPageListSidebar && (
              <MobilePageSidebarBtn
                onClick={() => emitAppEvent('workspace:togglePageSidebar')}
                title={t('workspace.pageList')}
              >
                <Menu size={18} />
              </MobilePageSidebarBtn>
            )}
          </TopBarLeft>
          <TopBarRight />
        </TopBar>
      )}

      <AppErrorBoundary name="workspace-route-content" resetKey={location.pathname}>
        {children({ inputSlot: pageInputSlot })}
      </AppErrorBoundary>

      {showComposer && !hasPageListSidebar && (
        <>
          <InputAreaWrapper>
            <ScrollToBottomBtn
              $visible={scrollBtnVisible}
              onClick={() => emitAppEvent('agent:scrollToBottom')}
            >
              ↓
            </ScrollToBottomBtn>
            <WorkspaceComposer />
          </InputAreaWrapper>
          {aiDisclaimerEnabled && (
            <DisclaimerFooter>
              {t('chat.aiDisclaimer')}
            </DisclaimerFooter>
          )}
        </>
      )}

      {dropZoneActive && dragging && <DropOverlay />}
    </Container>
  );
};
